import { useEffect, useState } from "react"
import apiClient from "../../lib/api"
import type { IPrayerRequest, ITestimony } from "@dc-ireland/shared"
import AdminAlert from "../../components/admin/AdminAlert"

type Filter = "" | "prayer" | "testimony"

const AdminPrayerRequests = () => {
  const [prayers, setPrayers] = useState<IPrayerRequest[]>([])
  const [testimonies, setTestimonies] = useState<ITestimony[]>([])
  const [prayerStats, setPrayerStats] = useState<{ total: number; recent: number } | null>(null)
  const [testimonyStats, setTestimonyStats] = useState<{ total: number; recent: number } | null>(null)
  const [filter, setFilter] = useState<Filter>("")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = async () => {
    try {
      setLoading(true)
      setError(null)
      const [prayerList, testimonyList, prayersOverview, testimoniesOverview] = await Promise.all([
        apiClient.getPrayerRequests(),
        apiClient.getTestimonies(),
        apiClient.getPrayerRequestStats(),
        apiClient.getTestimonyStats(),
      ])
      setPrayers(prayerList)
      setTestimonies(testimonyList)
      setPrayerStats(prayersOverview)
      setTestimonyStats(testimoniesOverview)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load submissions")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const handleDeletePrayer = async (id: string) => {
    if (!confirm("Delete this prayer request?")) return
    try {
      await apiClient.deletePrayerRequest(id)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete")
    }
  }

  const handleDeleteTestimony = async (id: string) => {
    if (!confirm("Delete this testimony?")) return
    try {
      await apiClient.deleteTestimony(id)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete")
    }
  }

  const showPrayers = filter === "" || filter === "prayer"
  const showTestimonies = filter === "" || filter === "testimony"
  const totalCount = (prayerStats?.total ?? 0) + (testimonyStats?.total ?? 0)
  const recentCount = (prayerStats?.recent ?? 0) + (testimonyStats?.recent ?? 0)

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Prayers & Testimonies</h2>

      {error && <AdminAlert variant="error" message={error} onDismiss={() => setError(null)} />}

      <div className="stats stats-vertical shadow lg:stats-horizontal">
        <div className="stat">
          <div className="stat-title">Total</div>
          <div className="stat-value text-primary">{totalCount}</div>
        </div>
        <div className="stat">
          <div className="stat-title">Prayers</div>
          <div className="stat-value">{prayerStats?.total ?? 0}</div>
        </div>
        <div className="stat">
          <div className="stat-title">Testimonies</div>
          <div className="stat-value">{testimonyStats?.total ?? 0}</div>
        </div>
        <div className="stat">
          <div className="stat-title">Last 7 days</div>
          <div className="stat-value">{recentCount}</div>
        </div>
      </div>

      <div className="flex gap-2">
        <button className={`btn btn-sm ${filter === "" ? "btn-primary" : "btn-ghost"}`} onClick={() => setFilter("")}>
          All
        </button>
        <button
          className={`btn btn-sm ${filter === "prayer" ? "btn-primary" : "btn-ghost"}`}
          onClick={() => setFilter("prayer")}
        >
          Prayers
        </button>
        <button
          className={`btn btn-sm ${filter === "testimony" ? "btn-primary" : "btn-ghost"}`}
          onClick={() => setFilter("testimony")}
        >
          Testimonies
        </button>
      </div>

      {loading ? (
        <span className="loading loading-spinner loading-md"></span>
      ) : (
        <div className="space-y-4">
          {showPrayers &&
            prayers.map((req) => (
              <div key={req.id} className="card bg-base-100 shadow">
                <div className="card-body">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <span className="badge badge-outline">Prayer</span>
                      {req.isAnonymous && <span className="badge badge-ghost ml-2">Anonymous</span>}
                      {req.county && <span className="badge badge-ghost ml-2">{req.county}</span>}
                      <h3 className="mt-2 font-semibold">{req.name}</h3>
                    </div>
                    {req.createdAt && (
                      <span className="text-sm text-secondary">
                        {new Date(req.createdAt).toLocaleString("en-IE")}
                      </span>
                    )}
                  </div>
                  <p className="mt-2 whitespace-pre-wrap">{req.message}</p>
                  {req.id && (
                    <div className="card-actions justify-end">
                      <button className="btn btn-error btn-sm" onClick={() => handleDeletePrayer(req.id!)}>
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}

          {showTestimonies &&
            testimonies.map((item) => (
              <div key={item.id} className="card bg-base-100 shadow">
                <div className="card-body">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <span className="badge badge-outline">Testimony</span>
                      {item.county && <span className="badge badge-ghost ml-2">{item.county}</span>}
                      <h3 className="mt-2 font-semibold">{item.name}</h3>
                    </div>
                    {item.createdAt && (
                      <span className="text-sm text-secondary">
                        {new Date(item.createdAt).toLocaleString("en-IE")}
                      </span>
                    )}
                  </div>
                  <p className="mt-2 whitespace-pre-wrap">{item.message}</p>
                  {item.id && (
                    <div className="card-actions justify-end">
                      <button className="btn btn-error btn-sm" onClick={() => handleDeleteTestimony(item.id!)}>
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}

          {!showPrayers && !showTestimonies && <p className="text-secondary">No submissions found.</p>}
          {showPrayers && showTestimonies && prayers.length === 0 && testimonies.length === 0 && (
            <p className="text-secondary">No submissions found.</p>
          )}
          {filter === "prayer" && prayers.length === 0 && <p className="text-secondary">No prayer requests found.</p>}
          {filter === "testimony" && testimonies.length === 0 && (
            <p className="text-secondary">No testimonies found.</p>
          )}
        </div>
      )}
    </div>
  )
}

export default AdminPrayerRequests
